/**
 * S3 Client
 * Low-level S3 operations using AWS SDK
 */

import { S3Client, PutObjectCommand, GetObjectCommand, DeleteObjectCommand } from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import authService from '../auth/authService';
import { getTenantConfig } from '../../config/tenantConfig';

/**
 * Get configured S3 client with Cognito credentials
 */
const getClient = async () => {
  const config = getTenantConfig();
  const credentials = await authService.getCredentials();

  return new S3Client({
    region: config.s3.region,
    credentials
  });
};

/**
 * Upload file to S3
 * @param {string} bucket - S3 bucket name
 * @param {string} key - Object key
 * @param {Blob|Buffer} data - File data
 * @param {Object} options - Upload options
 * @returns {Promise<void>}
 */
export const uploadFile = async (bucket, key, data, options = {}) => {
  const client = await getClient();

  const command = new PutObjectCommand({
    Bucket: bucket,
    Key: key,
    Body: data,
    ContentType: options.contentType || 'application/octet-stream',
    Metadata: options.metadata || {},
    ACL: options.acl || 'private'
  });

  await client.send(command);
};

/**
 * Get signed URL for file download
 * @param {string} bucket - S3 bucket name
 * @param {string} key - Object key
 * @param {number} expiresIn - URL expiration in seconds (default: 3600)
 * @returns {Promise<string>} Signed URL
 */
export const getSignedDownloadUrl = async (bucket, key, expiresIn = 3600) => {
  const client = await getClient();

  const command = new GetObjectCommand({
    Bucket: bucket,
    Key: key
  });

  return await getSignedUrl(client, command, { expiresIn });
};

/**
 * Delete file from S3
 * @param {string} bucket - S3 bucket name
 * @param {string} key - Object key
 * @returns {Promise<void>}
 */
export const deleteFile = async (bucket, key) => {
  const client = await getClient();

  const command = new DeleteObjectCommand({
    Bucket: bucket,
    Key: key
  });

  await client.send(command);
};

/**
 * Get public URL for file (if bucket allows public access)
 * @param {string} bucket - S3 bucket name
 * @param {string} key - Object key
 * @param {string} region - AWS region
 * @returns {string} Public URL
 */
export const getPublicUrl = (bucket, key, region) => {
  return `https://${bucket}.s3.${region}.amazonaws.com/${key}`;
};
